import React, { useState } from 'react';
import {
  AvailableIcons,
  Button,
  PaginationWithItemsPerPage,
  SearchInput,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { ControlSnapshotPanel } from '../ControlSnapshotPanel';
import { BlastRadiusBanner } from '../BlastRadiusBanner';
import { FullConfigDiffModal } from '../FullConfigDiffModal';
import { useMerchRulesEditor } from './useMerchRulesEditor';
import { RuleRow } from './RuleRow';
import { RuleEditDrawer } from './RuleEditDrawer';
import { NewRuleComposer } from './NewRuleComposer';
import { getRuleSummaryText } from './helpers';
import { MerchRulesEditorProps } from './types';
import './MerchRulesEditor.scss';

const CONTROL_SNAPSHOT_NOTE = "The Control column of the editor shows each rule's frozen state.";

export const MerchRulesEditor = (props: MerchRulesEditorProps): JSX.Element => {
  const {
    isLoading,
    hasError,
    visibleRows,
    pagedRows,
    pagination,
    setPagination,
    searchTerm,
    setSearchTerm,
    surfaceRulesCount,
    surfaceLabel,
    controlItems,
    editingRow,
    openEditDrawer,
    closeEditDrawer,
    toggleRuleActive,
    saveRuleEdit,
    resetToControl,
    stripAllRules,
    isBaseline,
    changeCount,
    newRules,
    removeNewRule,
    addRule,
    conditionFieldOptions,
  } = useMerchRulesEditor(props);
  const { testVariant } = props;
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [isFullDiffOpen, setIsFullDiffOpen] = useState(false);
  const changedEntities = testVariant?.versionChangesSummary?.changedEntities;

  if (isLoading) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Loading rules...
      </Typography>
    );
  }

  if (hasError) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Couldn&apos;t load merchandising rules for this surface. This can happen if the catalogue
        has no items yet — add items in Catalogue Manager, then come back to this step.
      </Typography>
    );
  }

  return (
    <div className='syte-merch-rules-editor'>
      <ControlSnapshotPanel items={controlItems} note={CONTROL_SNAPSHOT_NOTE} />

      <div className='syte-merch-rules-editor-variant'>
        <div className='syte-merch-rules-editor-variant-header'>
          <span className='syte-merch-rules-editor-variant-dot' />
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            {`Variant B · Merchandising rules on ${surfaceLabel}`}
          </Typography>
          <span className='syte-merch-rules-editor-variant-count'>
            {`${changeCount} change${changeCount === 1 ? '' : 's'}`}
          </span>
        </div>

        <div className='syte-merch-rules-editor-section-header'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            {`Existing rules on ${surfaceLabel}`}
          </Typography>
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-merch-rules-editor-section-subtitle'
          >
            {`${surfaceRulesCount} active · frozen at launch`}
          </Typography>
          <SearchInput
            placeholder='Search rules'
            value={searchTerm}
            onChange={value => setSearchTerm(value)}
            className='syte-merch-rules-editor-search'
          />
        </div>

        {surfaceRulesCount === 0 ? (
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
            This shop has no merchandising rules on this surface yet — new rules below will be the
            only difference from control.
          </Typography>
        ) : (
          <div className='syte-merch-rules-editor-table-scroll'>
            <table className='syte-merch-rules-editor-table'>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Control</th>
                  <th>In Variant B</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(row => (
                  <RuleRow
                    key={row.control.id}
                    row={row}
                    onToggleActive={toggleRuleActive}
                    onEdit={openEditDrawer}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {visibleRows.length > 0 && (
          <PaginationWithItemsPerPage
            skip={pagination.skip}
            limit={pagination.limit}
            totalItemsCount={visibleRows.length}
            onPaginationChange={setPagination}
          />
        )}

        {surfaceRulesCount > 0 && (
          <Button
            prefixId='variantsStep_merchRules_turnAllOff'
            variant='tertiary'
            className='syte-merch-rules-editor-baseline-link'
            onClick={() => stripAllRules()}
            disabled={isBaseline}
          >
            {isBaseline
              ? 'All rules on this surface are off (baseline test)'
              : `Turn all ${surfaceRulesCount} rules off (baseline test)`}
          </Button>
        )}

        <div className='syte-merch-rules-editor-section-header syte-merch-rules-editor-new-rules-header'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            {`New rules in Variant B`}
          </Typography>
        </div>

        {newRules.length === 0 && !isAddingRule ? (
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-merch-rules-editor-no-new-rules'
          >
            None yet. A new rule runs for Variant B shoppers only.
          </Typography>
        ) : (
          <div className='syte-merch-rules-editor-new-rules-list'>
            {newRules.map(rule => (
              <div key={rule.id} className='syte-merch-rule-new-row'>
                <div className='syte-merch-rule-new-row-content'>
                  <div className='syte-merch-rule-row-name'>{rule.name}</div>
                  <div className='syte-merch-rule-row-summary'>{getRuleSummaryText(rule)}</div>
                </div>
                <Button
                  prefixId={`variantsStep_merchRules_removeNewRule_${rule.id}`}
                  variant='tertiary'
                  onClick={() => removeNewRule(rule)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        {isAddingRule && (
          <NewRuleComposer
            fieldOptions={conditionFieldOptions}
            onApply={draft => {
              addRule(draft);
              setIsAddingRule(false);
            }}
            onCancel={() => setIsAddingRule(false)}
          />
        )}

        <div className='syte-merch-rules-editor-actions'>
          <Button
            prefixId='variantsStep_merchRules_newRule'
            variant='secondary'
            startIcon={AvailableIcons.Add}
            onClick={() => setIsAddingRule(true)}
            disabled={isAddingRule}
          >
            New rule
          </Button>
          <Button
            prefixId='variantsStep_merchRules_fullConfigDiff'
            variant='tertiary'
            onClick={() => setIsFullDiffOpen(true)}
            disabled={!changedEntities}
          >
            Full config diff
          </Button>
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-merch-rules-editor-actions-caption'
          >
            Changes apply to Variant B only.
          </Typography>
        </div>

        {changeCount > 1 && (
          <BlastRadiusBanner changeCount={changeCount} scopeLabel={surfaceLabel} />
        )}

        {/* TODO: results preview (top 5 for a sample product) — implement later */}
      </div>

      {editingRow && (
        <RuleEditDrawer
          row={editingRow}
          fieldOptions={conditionFieldOptions}
          onClose={closeEditDrawer}
          onSave={saveRuleEdit}
          onResetToControl={resetToControl}
        />
      )}

      {isFullDiffOpen && changedEntities && (
        <FullConfigDiffModal
          isOpen={isFullDiffOpen}
          onClose={() => setIsFullDiffOpen(false)}
          changedEntities={changedEntities}
          variantName={testVariant?.name}
        />
      )}
    </div>
  );
};
